import { Form, Button, InputGroup } from "react-bootstrap";
import { isLocationUnderParent } from "../../../utils/locationHelpers";

const CreateLoginScreen = ({
  mode = "establishment",
  doctorProfile = null,
  userProfile = null,
  users = [],
  selectedUserId,
  onUserChange,
  establishments = [],
  doctors = [],
  selectedEstablishmentId,
  selectedDoctorId,
  onEstablishmentChange,
  onDoctorChange,
  form,
  onChange,
  userTypes,
  locations = [],
  savedLogins,
  loginPasswordsById,
  onSave,
  onClose,
  onEdit,
  onDelete,
  onCancelEdit,
  saving,
}) => {
  const isDoctor = mode === "doctor";
  const isLocation = mode === "location";
  const isUser = mode === "user";
  const isEstablishment = mode === "establishment";
  const selectedId = isDoctor ? selectedDoctorId : selectedEstablishmentId;
  const lockedDoctor = isDoctor && doctorProfile;
  const isEditing = !!form?.login_id;
  const filteredUserTypes = isUser && userProfile
    ? userTypes.filter((ut) => String(ut.user_type_id) === String(userProfile.user_type_id))
    : isLocation
      ? userTypes.filter((ut) => /location/i.test(ut.user_type || ""))
      : userTypes;
  const formEnabled = isUser
    ? !!userProfile && filteredUserTypes.length > 0
    : isLocation
      ? filteredUserTypes.length > 0
      : !!selectedId;
  const showSavedLoginsList = isEstablishment || isDoctor || formEnabled;
  const countries = isLocation
    ? locations.filter((location) => /country/i.test(location.location_type || ""))
    : [];
  const filteredLocations = isLocation && form.country_id
    ? locations.filter(
        (location) =>
          /(city|district)/i.test(location.location_type || "")
          && isLocationUnderParent(locations, location.location_id, form.country_id)
      )
    : [];

  const phonePrefix = isUser && userProfile?.country_code ? userProfile.country_code : "";

  const getEstablishmentLabel = (establishmentId) => {
    if (!establishmentId) return "—";
    const est = establishments.find(
      (item) => String(item.establishment_id) === String(establishmentId)
    );
    return est?.establishment_name || est?.legal_entity_name || `Establishment #${establishmentId}`;
  };

  const getDoctorLabel = (doctorId) => {
    if (!doctorId) return "—";
    const doc = doctors.find((item) => String(item.doctor_id) === String(doctorId));
    return doc?.doctor_name || `Doctor #${doctorId}`;
  };

  return (
  <div className="create-login-screen">
    <div className="create-login-screen__card">
      <h5 className="create-login-screen__section-title">
        {isEditing ? "Edit Login Credentials" : "Login Credentials"}
      </h5>
      {isUser && userProfile && (
        <p className="create-login-screen__subtitle mb-3">
          {userProfile.user_name}
          {userProfile.user_type ? ` · ${userProfile.user_type}` : ""}
          {userProfile.user_id ? ` · User ID ${userProfile.user_id}` : ""}
        </p>
      )}
      <Form className="create-login-screen__form">
        {isUser && (
          <Form.Group className="mb-3">
            <Form.Label>User <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={selectedUserId || ""}
              onChange={(e) => onUserChange(e.target.value)}
              disabled={isEditing}
            >
              <option value="">Select user</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.user_name || `User #${u.user_id}`}
                  {u.user_type ? ` · ${u.user_type}` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {isUser && userProfile?.establishment_name && (
          <Form.Group className="mb-3">
            <Form.Label>Establishment</Form.Label>
            <Form.Control
              type="text"
              readOnly
              className="em-field-locked"
              value={userProfile.establishment_name}
            />
          </Form.Group>
        )}

        {!lockedDoctor && !isLocation && !isUser && (
          <Form.Group className="mb-3">
            <Form.Label>
              {isDoctor ? "Doctor" : "Establishment"} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={selectedId || ""}
              onChange={(e) => (
                isDoctor
                  ? onDoctorChange(e.target.value)
                  : onEstablishmentChange(e.target.value)
              )}
              disabled={isEditing}
            >
              <option value="">{isDoctor ? "Select doctor" : "Select establishment"}</option>
              {isDoctor
                ? doctors.map((doc) => (
                    <option key={doc.doctor_id} value={doc.doctor_id}>
                      {doc.doctor_name || `Doctor #${doc.doctor_id}`}
                    </option>
                  ))
                : establishments.map((est) => (
                    <option key={est.establishment_id} value={est.establishment_id}>
                      {est.establishment_name || est.legal_entity_name || `Establishment #${est.establishment_id}`}
                    </option>
                  ))}
            </Form.Select>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>User Type <span className="text-danger">*</span></Form.Label>
          <Form.Select
            value={form.user_type_id || ""}
            onChange={(e) => onChange("user_type_id", e.target.value)}
            disabled={!formEnabled || isUser}
            className={isUser ? "em-field-locked" : ""}
          >
            <option value="">Select user type</option>
            {filteredUserTypes.map((ut) => (
              <option key={ut.user_type_id} value={ut.user_type_id}>
                {ut.user_type}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {isLocation && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Country <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={form.country_id || ""}
                onChange={(e) => {
                  const countryId = e.target.value;
                  const selectedCountry = countries.find(
                    (country) => String(country.location_id) === String(countryId)
                  );
                  onChange("country_id", countryId);
                  onChange("country_code", selectedCountry?.country_code || "");
                  onChange("location_ids", []);
                }}
                disabled={!formEnabled}
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.location_id} value={country.location_id}>
                    {country.location_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location <span className="text-danger">*</span></Form.Label>
              <div
                className="border rounded p-3"
                style={{ maxHeight: 180, overflowY: "auto" }}
              >
                {filteredLocations.length > 0 ? (
                  filteredLocations.map((location) => {
                    const isChecked = (form.location_ids || []).some(
                      (id) => String(id) === String(location.location_id)
                    );
                    return (
                      <Form.Check
                        key={location.location_id}
                        type="checkbox"
                        id={`location-login-${location.location_id}`}
                        label={location.location_name}
                        checked={isChecked}
                        disabled={!formEnabled || !form.country_id}
                        onChange={(e) => {
                          const locationId = Number(location.location_id);
                          const selectedIds = form.location_ids || [];
                          onChange(
                            "location_ids",
                            e.target.checked
                              ? [...selectedIds, locationId]
                              : selectedIds.filter(
                                  (id) => String(id) !== String(location.location_id)
                                )
                          );
                        }}
                      />
                    );
                  })
                ) : (
                  <span className="text-muted">
                    {form.country_id
                      ? "No City/District locations are configured for this country."
                      : "Select a country first."}
                  </span>
                )}
              </div>
              <Form.Text className="text-muted">
                Select one or more locations.
              </Form.Text>
            </Form.Group>
          </>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter name"
            value={form.user_name || ""}
            onChange={(e) => onChange("user_name", e.target.value)}
            disabled={!formEnabled}
          />
        </Form.Group>

        {isLocation && (
          <Form.Group className="mb-3">
            <Form.Label>Country Code</Form.Label>
            <Form.Control
              type="text"
              value={form.country_code || ""}
              readOnly
              className="em-field-locked"
              placeholder="Selected country's code"
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Phone No <span className="text-danger">*</span></Form.Label>
          {phonePrefix ? (
            <InputGroup>
              <InputGroup.Text>{phonePrefix}</InputGroup.Text>
              <Form.Control
                type="text"
                maxLength={15}
                placeholder="Phone number"
                value={form.mobile_no || ""}
                onChange={(e) => onChange("mobile_no", e.target.value)}
                disabled={!formEnabled}
              />
            </InputGroup>
          ) : (
            <Form.Control
              type="text"
              maxLength={15}
              placeholder="Phone number"
              value={form.mobile_no || ""}
              onChange={(e) => onChange("mobile_no", e.target.value)}
              disabled={!formEnabled}
            />
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            type="email"
            placeholder="Email address"
            value={form.email_address || ""}
            onChange={(e) => onChange("email_address", e.target.value)}
            disabled={!formEnabled}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Payout %</Form.Label>
          <Form.Control
            type="number"
            placeholder="e.g. 15"
            min="0"
            max="100"
            step="0.01"
            value={form.payout_percentage ?? ""}
            onChange={(e) => onChange("payout_percentage", e.target.value)}
            disabled={!formEnabled}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>
            {isEditing ? "Password (unchanged unless regenerated)" : "Password (System Generated)"}
          </Form.Label>
          <Form.Control
            type="text"
            value={form.password || (isEditing ? "••••••••" : "")}
            readOnly
            className="create-login-screen__password"
          />
        </Form.Group>

        <div className="create-login-screen__actions">
          {isEditing && onCancelEdit && (
            <Button
              type="button"
              variant="outline-secondary"
              className="me-2"
              onClick={onCancelEdit}
              disabled={saving}
            >
              Cancel Edit
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            className="create-login-screen__save"
            onClick={onSave}
            disabled={saving || !formEnabled}
          >
            {saving ? (isEditing ? "Updating…" : "Saving…") : (isEditing ? "Update" : "Save")}
          </Button>
        </div>
      </Form>
    </div>

    <div className="authdata-listview create-login-screen__list">
      <h6 className="authdata-listview-title">Saved Logins</h6>
      {!showSavedLoginsList ? (
        <p className="create-login-screen__empty">
          {isUser
            ? "Select a user from User Master to create a login."
            : isLocation
              ? "No location admin user type configured yet."
              : `Select ${isDoctor ? "a doctor" : "an establishment"} to view saved logins.`}
        </p>
      ) : savedLogins.length === 0 ? (
        <p className="create-login-screen__empty">
          {isEstablishment && selectedId
            ? "No logins created yet for this establishment."
            : isDoctor && selectedId
              ? "No logins created yet for this doctor."
              : "No logins created yet."}
        </p>
      ) : (
        <div className="authdata-table-scroll">
          <table className="authdata-table authdata-table--wide create-login-screen__table">
            <thead>
              <tr>
                <th>#</th>
                {isEstablishment && !selectedId ? <th>Establishment</th> : null}
                {isDoctor && !selectedId ? <th>Doctor</th> : null}
                <th>User Type</th>
                <th>Name</th>
                <th>Phone No</th>
                <th>Email</th>
                <th>Payout %</th>
                <th>Password</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {savedLogins.map((log, i) => (
                <tr key={log.login_id}>
                  <td data-label="#">{i + 1}</td>
                  {isEstablishment && !selectedId ? (
                    <td data-label="Establishment">
                      {getEstablishmentLabel(log.establishment_id)}
                    </td>
                  ) : null}
                  {isDoctor && !selectedId ? (
                    <td data-label="Doctor">{getDoctorLabel(log.doctor_id)}</td>
                  ) : null}
                  <td data-label="User Type">{log.user_type || "—"}</td>
                  <td data-label="Name">{log.user_name || "—"}</td>
                  <td data-label="Phone No">
                    {isUser && phonePrefix && log.mobile_no
                      ? `${phonePrefix} ${log.mobile_no}`
                      : (log.mobile_no || "—")}
                  </td>
                  <td data-label="Email">{log.email_address || "—"}</td>
                  <td data-label="Payout %">
                    {log.payout_percentage != null && log.payout_percentage !== ""
                      ? `${log.payout_percentage}%`
                      : "—"}
                  </td>
                  <td data-label="Password">{loginPasswordsById[log.login_id] || "—"}</td>
                  <td data-label="Actions" className="authdata-actions">
                    <button
                      type="button"
                      className="btn-action edit"
                      onClick={() => onEdit?.(log)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-action delete"
                      onClick={() => onDelete?.(log.login_id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
  );
};

export default CreateLoginScreen;
